/*
 * HG-142 Mix & Match box builder (sections/mix-match-builder.liquid).
 *
 * The box state lives here and updates instantly on every tap. Easy Bundle's SDK
 * (window.gbbSdk) is the engine: every change is mirrored into its bundle cart,
 * and addBundleToCart() writes the box to the Shopify cart as one discounted line.
 * Taps before the SDK has loaded are kept and replayed once it is ready.
 * Docs: https://eb-sdk-doc-v2.netlify.app/
 *
 * Share links: ?box=<variantId>x<qty>.<variantId> fills the box (qty 1 can be left
 * out), ?quickbox=<metaobject handle> applies a quick box. Both replace nothing else
 * in the URL and are removed after loading, so a reload keeps the shopper's edits.
 *
 * Events (docs/tracking-mix-match.md): each one goes to Shopify's event bus as
 * heimat_mm_<name> (a custom pixel forwards it to GA4), to PostHog once the consent
 * gate has loaded it, to Klaviyo when marketing consent is given, and to the page as
 * a mix-match:<name> DOM event.
 */
if (!customElements.get('mix-match-builder')) {
  customElements.define('mix-match-builder', class MixMatchBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dataset.jsAt = Math.round(performance.now()); // when cards became tappable, read by the load-time measurements in the PR
      this.strings = JSON.parse(this.querySelector('[data-mm-strings]').textContent);
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
      this.freeShipping = Number(this.dataset.freeShipping) || 0; // cents, 0 = no marker
      this.box = this.querySelector('[data-mm-box]');
      this.qty = new Map(); // variantId -> quantity in the box
      this.products = new Map(); // variantId -> { title, unit, price, image, available }
      this.routes = new Map(); // variantId -> { categoryId, stepId } inside the SDK bundle
      this.filters = new Set(); // active filter tags
      this.sdk = null;
      this.sdkCart = null;
      this.lastReached = -1;
      this.trackedMilestones = new Set();

      // Every pack button describes one variant; a card without a switcher still has one.
      this.controls = [...this.querySelectorAll('[data-mm-control]')].map((control) => {
        const card = control.closest('.hg-card');
        const packs = [...card.querySelectorAll('[data-mm-pack]')];
        packs.forEach((pack) => {
          const id = pack.dataset.variant;
          if (this.products.has(id)) return;
          this.products.set(id, {
            title: control.dataset.title,
            unit: pack.dataset.unit,
            price: Number(pack.dataset.price),
            image: pack.dataset.image,
            available: pack.dataset.available === 'true',
          });
        });
        return { control, card, packs, group: card.querySelector('[data-mm-packs]') };
      });

      this.restore();
      this.loadFromLink();
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.setupTabs();
      this.setupFilters();
      this.setupTuck();
      this.buildMarkers();
      this.render({ quiet: true });
      this.waitForSdk();
    }

    /* ---------- state ---------- */

    get count() {
      let total = 0;
      this.qty.forEach((q) => { total += q; });
      return total;
    }

    get subtotal() {
      let total = 0;
      this.qty.forEach((q, id) => { total += q * (this.products.get(id)?.price || 0); });
      return total;
    }

    tierIndex(subtotal) {
      let index = -1;
      this.tiers.forEach((tier, i) => { if (subtotal >= tier.min) index = i; });
      return index;
    }

    // Box value before discount at which the discounted total reaches free shipping.
    // The cart transform lowers the box price, so free shipping comes later than the
    // threshold itself: €39 needs €43.34 at 10 % off.
    shippingAt() {
      if (!this.freeShipping) return 0;
      const bands = [{ min: 0, percent: 0 }, ...this.tiers];
      for (let i = 0; i < bands.length; i++) {
        const needed = Math.ceil((this.freeShipping * 100) / (100 - bands[i].percent));
        if (needed < bands[i].min) return bands[i].min;
        if (i === bands.length - 1 || needed < bands[i + 1].min) return needed;
      }
      return this.freeShipping;
    }

    // Tiers and free shipping, ordered by the box value that reaches them.
    milestones() {
      const list = this.tiers.map((tier) => ({ type: 'tier', at: tier.min, percent: tier.percent }));
      if (this.freeShipping) list.push({ type: 'shipping', at: this.shippingAt() });
      return list.sort((a, b) => a.at - b.at);
    }

    // The rest of the milestones are spaced evenly (20 % apart) after the first one, so
    // markers close in value (€43.34 shipping, €45 tier) never overlap.
    markerPosition(index, count) {
      const first = Math.max(40, 100 - (count - 1) * 20);
      return index === 0 ? first : first + ((100 - first) * index) / (count - 1);
    }

    fillPosition(subtotal, milestones) {
      if (!milestones.length) return 0;
      const count = milestones.length;
      if (subtotal <= milestones[0].at) return (subtotal / milestones[0].at) * this.markerPosition(0, count);
      for (let i = 0; i < count - 1; i++) {
        const from = milestones[i];
        const to = milestones[i + 1];
        if (subtotal < to.at) {
          const start = this.markerPosition(i, count);
          return start + (this.markerPosition(i + 1, count) - start) * ((subtotal - from.at) / (to.at - from.at));
        }
      }
      return 100;
    }

    setQty(variantId, quantity, { track = true } = {}) {
      const product = this.products.get(variantId);
      const before = this.qty.get(variantId) || 0;
      if (!product || (quantity > before && !product.available)) return;
      if (quantity > 0) this.qty.set(variantId, quantity);
      else this.qty.delete(variantId);
      this.hideError();
      this.persist();
      this.render();
      this.syncVariant(variantId);
      if (track && quantity > before) {
        this.track('snack_added', { ...this.itemData(variantId, quantity), added: quantity - before });
      }
    }

    persist() {
      try {
        localStorage.setItem(this.dataset.storageKey, JSON.stringify([...this.qty]));
      } catch (error) { /* private mode: the box just does not survive a reload */ }
    }

    restore() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.dataset.storageKey) || '[]');
        saved.forEach(([id, q]) => {
          if (this.products.get(id)?.available && q > 0) this.qty.set(id, q);
        });
      } catch (error) { /* ignore a corrupt entry */ }
    }

    /* ---------- events ---------- */

    onClick(event) {
      const pack = event.target.closest('[data-mm-pack]');
      if (pack) return this.selectPack(pack);
      const filter = event.target.closest('[data-mm-filter]');
      if (filter) return this.toggleFilter(filter);
      const quick = event.target.closest('[data-mm-quick]');
      if (quick) return this.applyQuickBox(quick);
      const step = event.target.closest('[data-mm-step]');
      if (step && !step.disabled) {
        const holder = step.closest('[data-variant]');
        const id = holder.dataset.variant;
        this.setQty(id, (this.qty.get(id) || 0) + Number(step.dataset.mmStep));
        if (step.dataset.mmStep === '1') this.bump(holder);
        return;
      }
      if (event.target.closest('[data-mm-toggle]')) return this.toggleBox();
      if (event.target.closest('[data-mm-share]')) return this.share();
      if (event.target.closest('[data-mm-clear]')) {
        [...this.qty.keys()].forEach((id) => this.setQty(id, 0));
        return;
      }
      if (event.target.closest('[data-mm-atc]')) return this.addToCart();
      // Phones: the docked bar is one big tap target for the full-screen box, apart from
      // the cart button. The chevron button stays the keyboard and screen reader path.
      if (event.target.closest('[data-mm-box]') && !event.target.closest('[data-mm-toast]') && !this.box.classList.contains('is-open')
        && !this.box.classList.contains('is-empty') && window.matchMedia('(max-width: 989px)').matches) {
        this.toggleBox(true);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.box.classList.contains('is-open')) {
        this.toggleBox(false);
        this.querySelector('[data-mm-toggle]').focus();
        return;
      }
      // Arrow keys move between packs, like a radio group.
      const pack = event.target.closest('[data-mm-pack]');
      const offset = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!pack || !offset) return;
      event.preventDefault();
      const packs = [...pack.parentElement.querySelectorAll('[data-mm-pack]:not([hidden]):not(:disabled)')];
      if (!packs.length) return;
      const next = packs[(packs.indexOf(pack) + offset + packs.length) % packs.length];
      this.selectPack(next);
      next.focus();
    }

    selectPack(pack, { allowUnavailable = false } = {}) {
      const entry = this.controls.find((item) => item.packs.includes(pack));
      if (!entry || pack.hidden || (pack.disabled && !allowUnavailable)) return;
      const { control, card, packs } = entry;
      packs.forEach((other) => {
        const active = other === pack;
        other.setAttribute('aria-checked', active);
        other.tabIndex = active ? 0 : -1;
      });
      const available = pack.dataset.available === 'true';
      control.dataset.variant = pack.dataset.variant;
      control.dataset.available = available;
      control.querySelector('.mm-control__add').disabled = !available;
      card.dataset.cardVariant = pack.dataset.variant;
      card.querySelector('[data-mm-price]').textContent = pack.dataset.priceText;
      const compare = card.querySelector('[data-mm-compare]');
      if (compare) {
        compare.hidden = !pack.dataset.compareText;
        compare.querySelector('[data-mm-compare-text]').textContent = pack.dataset.compareText;
      }
      const unitPrice = card.querySelector('[data-mm-unit-price]');
      if (unitPrice) unitPrice.textContent = pack.dataset.unitPrice;
      // Multipacks have their own packshot (the "10x" is in the image).
      const image = card.querySelector('.hg-card__image-link img');
      if (image && pack.dataset.srcset) {
        image.srcset = pack.dataset.srcset;
        image.src = pack.dataset.srcset.split(' ')[0];
      }
      this.render({ quiet: true });
    }

    // Quick box: adds its packs on top of what is already in the box. Packs that are sold
    // out or not part of the Easy Bundle bundle are skipped, and the toast names them.
    applyQuickBox(button) {
      const items = JSON.parse(button.parentElement.querySelector('[data-mm-quick-items]').textContent || '[]');
      const skipped = [];
      items.forEach((item) => {
        const id = String(item.variant);
        if (!this.products.has(id)) {
          this.products.set(id, { title: item.title, unit: item.unit, price: Number(item.price), image: item.image, available: item.available });
        }
        if (!item.available || (this.sdk && !this.routes.has(id))) {
          skipped.push(item.title);
          return;
        }
        this.setQty(id, (this.qty.get(id) || 0) + Number(item.qty || 1), { track: false });
      });
      let text = this.fill(this.strings.quickAdded, { box: button.dataset.title });
      if (skipped.length) text += ' ' + this.fill(this.strings.quickSkipped, { products: [...new Set(skipped)].join(', ') });
      this.toast(text);
      this.bump(this.querySelector('[data-mm-count]'));
      this.track('quick_box_applied', { quick_box: button.dataset.handle || button.dataset.title, quick_box_title: button.dataset.title, skipped: skipped.length });
    }

    /* ---------- share links ---------- */

    loadFromLink() {
      const url = new URL(window.location.href);
      const boxParam = url.searchParams.get('box');
      const quickParam = url.searchParams.get('quickbox');
      if (!boxParam && !quickParam) return;
      url.searchParams.delete('box');
      url.searchParams.delete('quickbox');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);

      if (quickParam) {
        const button = [...this.querySelectorAll('[data-mm-quick]')].find((quick) => quick.dataset.handle === quickParam);
        if (button && !button.disabled) {
          this.qty.clear();
          this.applyQuickBox(button);
        }
        return;
      }
      // A shared box replaces the one in progress: the link is what the shopper came for.
      const wanted = boxParam.split('.').map((part) => part.match(/^(\d+)(?:x(\d+))?$/)).filter(Boolean);
      if (!wanted.length) return;
      this.qty.clear();
      const skipped = [];
      wanted.forEach(([, id, qty]) => {
        const product = this.products.get(id);
        if (!product?.available) {
          if (product) skipped.push(product.title);
          return;
        }
        this.qty.set(id, Math.min(50, (this.qty.get(id) || 0) + Number(qty || 1)));
      });
      this.persist();
      let text = this.strings.linkLoaded;
      if (skipped.length) text += ' ' + this.fill(this.strings.quickSkipped, { products: [...new Set(skipped)].join(', ') });
      this.toast(text);
      this.track('box_link_opened', { ...this.boxData(), skipped: skipped.length });
    }

    shareUrl() {
      const url = new URL(window.location.href);
      ['box', 'quickbox', 'preview_theme_id', '_pos', '_sid', '_ss'].forEach((key) => url.searchParams.delete(key));
      // Keep preview links working while the page runs on an unpublished theme.
      const theme = window.Shopify?.theme;
      if (theme?.id && theme.role && theme.role !== 'main') url.searchParams.set('preview_theme_id', theme.id);
      const value = [...this.qty].map(([id, q]) => (q > 1 ? `${id}x${q}` : id)).join('.');
      url.searchParams.set('box', value);
      url.hash = '';
      return url.toString();
    }

    async share() {
      if (!this.count) return;
      const url = this.shareUrl();
      this.track('box_shared', this.boxData());
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ title: this.strings.shareTitle, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        this.toast(this.strings.shareCopied);
      } catch (error) {
        this.toast(this.fill(this.strings.shareFailed, { url }));
      }
    }

    /* ---------- events ---------- */

    itemData(variantId, quantity) {
      const product = this.products.get(variantId) || {};
      return {
        variant_id: variantId,
        product_title: product.title,
        pack: product.unit,
        price: (product.price || 0) / 100,
        quantity,
        box_count: this.count,
        box_value: this.subtotal / 100,
      };
    }

    boxData() {
      const tier = this.tierIndex(this.subtotal);
      return {
        box_count: this.count,
        box_value: this.subtotal / 100,
        discount_percent: tier >= 0 ? this.tiers[tier].percent : 0,
        items: [...this.qty].map(([id, q]) => {
          const product = this.products.get(id) || {};
          return { variant_id: id, product_title: product.title, pack: product.unit, price: (product.price || 0) / 100, quantity: q };
        }),
      };
    }

    track(name, data) {
      if (window.Shopify?.designMode) return;
      const payload = { ...data, currency: window.Shopify?.currency?.active || 'EUR', list_source: 'mix_match_builder' };
      this.dispatchEvent(new CustomEvent(`mix-match:${name}`, { bubbles: true, detail: payload }));
      try {
        // Not tracking by itself: the forwarding pixel only runs with analytics consent.
        window.Shopify?.analytics?.publish?.(`heimat_mm_${name}`, payload);
      } catch (error) { /* analytics must never break the box */ }
      try {
        // window.posthog only exists after the consent gate has loaded it.
        if (typeof window.posthog?.capture === 'function') window.posthog.capture(`mix_match_${name}`, payload);
      } catch (error) { /* ignore */ }
      try {
        const privacy = window.Shopify?.customerPrivacy;
        if (window.klaviyo && typeof privacy?.marketingAllowed === 'function' && privacy.marketingAllowed()) {
          const { items, ...flat } = payload;
          window.klaviyo.push(['track', `Mix & Match ${name.replace(/_/g, ' ')}`, { ...flat, item_count: items?.length }]);
        }
      } catch (error) { /* ignore */ }
    }

    toast(text) {
      const toast = this.querySelector('[data-mm-toast]');
      toast.textContent = text;
      toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
    }

    setupFilters() {
      const group = this.querySelector('[data-mm-filters]');
      if (group) group.hidden = false;
      this.items = [...this.querySelectorAll('.mm__item')];
    }

    toggleFilter(button) {
      const key = button.dataset.mmFilter;
      const on = !this.filters.has(key);
      if (on) this.filters.add(key);
      else this.filters.delete(key);
      button.setAttribute('aria-pressed', on);
      this.items.forEach((item) => {
        const tags = (item.dataset.tags || '').split(' ');
        item.classList.toggle('is-filtered', ![...this.filters].every((tag) => tags.includes(tag)));
      });
      this.checkEmpty();
    }

    checkEmpty() {
      // Tabs hide cards by CSS, so ask the layout which cards are left.
      const notice = this.querySelector('[data-mm-no-results]');
      if (notice) notice.hidden = !this.items?.length || this.items.some((item) => item.offsetParent !== null);
    }

    setupTabs() {
      // One grid; data-active tells the section's CSS which tab's cards to show and in
      // which order. Without JS the same CSS follows the #mm-cN anchors instead.
      const tabs = [...this.querySelectorAll('[data-mm-tab]')];
      if (!tabs.length) return;
      const select = (tab, focus) => {
        tabs.forEach((other) => {
          const active = other === tab;
          other.setAttribute('aria-selected', active);
          other.tabIndex = active ? 0 : -1;
        });
        this.dataset.active = tab.dataset.mmTab;
        this.checkEmpty?.();
        if (focus) tab.focus();
        const strip = tab.closest('.mm__tabs-scroll');
        strip.scrollTo({ left: tab.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          select(tab);
          const shop = this.querySelector('.mm__shop');
          if (this.querySelector('.mm__tabs').getBoundingClientRect().top <= 1) shop.scrollIntoView({ behavior: 'smooth' });
        });
        tab.addEventListener('keydown', (event) => {
          const offset = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!offset) return;
          event.preventDefault();
          select(tabs[(index + offset + tabs.length) % tabs.length], true);
        });
      });
      const fromHash = tabs.find((tab) => tab.getAttribute('href') === window.location.hash);
      select(fromHash || tabs[0]);
    }

    setupTuck() {
      // Phones and tablets: once the tabs stick, they slide away while scrolling down and
      // come back on any scroll up, so the grid gets the screen.
      const tabs = this.querySelector('.mm__tabs');
      if (!tabs) return;
      const compact = window.matchMedia('(max-width: 989px)');
      let lastY = window.scrollY;
      let queued = false;
      const update = () => {
        queued = false;
        const delta = window.scrollY - lastY;
        if (Math.abs(delta) < 8) return; // collect small moves until they add up
        lastY = window.scrollY;
        const stuck = tabs.getBoundingClientRect().top <= 1;
        tabs.classList.toggle('is-tucked', compact.matches && stuck && delta > 0);
      };
      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(update);
      }, { passive: true });
      tabs.addEventListener('focusin', () => tabs.classList.remove('is-tucked'));
    }

    toggleBox(force) {
      const toggle = this.querySelector('[data-mm-toggle]');
      const open = force ?? toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', open);
      this.box.classList.toggle('is-open', open);
      // On phones the open box covers the screen; stop the page behind it from scrolling.
      document.documentElement.classList.toggle('mm-box-open', open);
      this.querySelector('[data-mm-toggle-label]').textContent = open ? this.strings.hideBox : this.strings.showBox;
    }

    bump(holder) {
      const count = this.querySelector('[data-mm-count]');
      [...new Set([holder, count])].forEach((el) => {
        el.classList.remove('is-bumped');
        void el.offsetWidth; // restart the animation on rapid taps
        el.classList.add('is-bumped');
      });
    }

    /* ---------- rendering ---------- */

    money(cents, { trim = false } = {}) {
      const text = this.formatMoney(cents);
      // Thresholds read better as "€35" than "€35,00".
      return trim && Math.round(cents) % 100 === 0 ? text.replace(/[.,]00(?!\d)/, '') : text;
    }

    formatMoney(cents) {
      // Self-contained: this script runs before the theme's money helpers have loaded.
      const amount = Math.round(cents) / 100;
      const number = (decimals, separator, thousands) => {
        const [whole, fraction] = amount.toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? separator + fraction : '');
      };
      const formats = {
        amount: () => number(2, '.', ','),
        amount_no_decimals: () => number(0, '.', ','),
        amount_with_comma_separator: () => number(2, ',', '.'),
        amount_no_decimals_with_comma_separator: () => number(0, ',', '.'),
        amount_with_apostrophe_separator: () => number(2, '.', "'"),
      };
      const format = (this.dataset.moneyFormat || '{{amount_with_comma_separator}} €').replace(/<[^>]*>/g, '');
      return format.replace(/{{\s*(\w+)\s*}}/, (match, key) => (formats[key] || formats.amount)());
    }

    fill(template, values) {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, key) => (key in values ? values[key] : match));
    }

    render({ quiet = false } = {}) {
      const subtotal = this.subtotal;
      const count = this.count;
      const tier = this.tierIndex(subtotal);
      // Prefer the SDK total once it agrees with our box, it is what the cart transform charges.
      const inSync = this.sdkCart && Math.abs((this.sdkCart.total_price || 0) - subtotal) < 1;
      const sdkTotal = inSync ? Math.round(this.sdkCart.discounted_price ?? subtotal) : null;
      const total = sdkTotal ?? (tier >= 0 ? Math.round(subtotal * (100 - this.tiers[tier].percent) / 100) : subtotal);

      this.controls.forEach(({ control, card, packs }) => {
        const q = this.qty.get(control.dataset.variant) || 0;
        let inBox = 0;
        packs.forEach((pack) => {
          const packQty = this.qty.get(pack.dataset.variant) || 0;
          inBox += packQty;
          const badge = pack.querySelector('[data-mm-pack-qty]');
          if (badge.textContent !== String(packQty || '')) badge.textContent = packQty || '';
        });
        control.dataset.qty = q;
        control.querySelector('[data-mm-qty]').textContent = q;
        control.querySelector('[data-mm-in-box]').textContent = this.fill(this.strings.inBox, { count: inBox });
        card.classList.toggle('mm-in-box', inBox > 0);
        card.classList.toggle('mm-soldout', control.dataset.available !== 'true');
      });

      this.querySelector('[data-mm-count]').textContent = this.fill(count === 1 ? this.strings.countOne : this.strings.countOther, { count });
      this.box.classList.toggle('is-empty', count === 0);
      this.querySelector('[data-mm-empty]').hidden = count > 0;
      this.querySelector('[data-mm-actions]').hidden = count === 0;
      this.renderList();

      const subtotalEl = this.querySelector('[data-mm-subtotal]');
      subtotalEl.hidden = total >= subtotal;
      subtotalEl.textContent = this.money(subtotal);
      this.querySelector('[data-mm-total]').textContent = this.money(total);
      const saving = this.querySelector('[data-mm-saving]');
      saving.hidden = total >= subtotal;
      saving.textContent = this.fill(this.strings.saving, { amount: this.money(subtotal - total) });

      this.renderProgress(subtotal, tier, quiet);
      if (!this.busy) this.querySelector('[data-mm-atc]').disabled = count === 0;
    }

    buildMarkers() {
      const track = this.querySelector('[data-mm-track]');
      track.querySelectorAll('[data-mm-marker]').forEach((marker) => marker.remove());
      const milestones = this.milestones();
      milestones.forEach((milestone, i) => {
        const marker = document.createElement('span');
        marker.className = `mm-box__marker mm-box__marker--${milestone.type}`;
        marker.dataset.mmMarker = '';
        marker.style.setProperty('--at', this.markerPosition(i, milestones.length) + '%');
        if (milestone.type === 'shipping') {
          const label = this.fill(this.strings.shippingMarker, { amount: this.money(this.freeShipping, { trim: true }) });
          marker.title = label;
          marker.innerHTML = `<svg viewBox="0 0 20 16" width="15" height="12" aria-hidden="true" focusable="false"><path d="M1.5 2.5h10v8h-10zM11.5 5.5h3.6l2.9 3v2h-6.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="5" cy="12.5" r="1.8" fill="currentColor"/><circle cx="14.5" cy="12.5" r="1.8" fill="currentColor"/></svg><span class="visually-hidden"></span>`;
          marker.querySelector('.visually-hidden').textContent = label;
        } else {
          marker.innerHTML = `<span class="mm-box__marker-label">−${milestone.percent}&nbsp;%</span>`;
        }
        track.appendChild(marker);
      });
      this.lastMilestones = milestones;
    }

    renderProgress(subtotal, tier, quiet) {
      const milestones = this.lastMilestones || this.milestones();
      this.querySelector('[data-mm-fill]').style.width = this.fillPosition(subtotal, milestones) + '%';
      let reached = -1;
      this.querySelectorAll('[data-mm-marker]').forEach((marker, i) => {
        const on = subtotal >= milestones[i].at;
        marker.classList.toggle('is-reached', on);
        if (on) reached = i;
      });
      this.box.dataset.tier = tier + 1; // 0 = no discount yet

      // One highlighted lead for what is active, then the next goal.
      const shippingAt = this.freeShipping ? this.shippingAt() : 0;
      const shipping = this.freeShipping > 0 && subtotal >= shippingAt;
      const next = milestones.find((milestone) => milestone.at > subtotal);
      const percent = tier >= 0 ? this.tiers[tier].percent : 0;
      let lead = '';
      if (tier >= 0 && shipping) lead = this.fill(next ? this.strings.leadTierShipping : this.strings.leadMaxShipping, { percent });
      else if (tier >= 0) lead = this.fill(next ? this.strings.leadTier : this.strings.leadMax, { percent });
      else if (shipping) lead = this.strings.leadShipping;
      let rest = '';
      if (subtotal === 0 && this.tiers.length) {
        rest = this.fill(this.strings.messageStart, { amount: this.money(this.tiers[0].min, { trim: true }), percent: this.tiers[0].percent });
      } else if (next?.type === 'tier') {
        rest = this.fill(this.strings.messageNextTier, { amount: this.money(next.at - subtotal), percent: next.percent });
      } else if (next?.type === 'shipping') {
        rest = this.fill(this.strings.messageNextShipping, { amount: this.money(next.at - subtotal) });
      }
      const message = this.querySelector('[data-mm-message]');
      const key = `${lead}|${rest}`;
      if (message.dataset.key !== key) {
        message.dataset.key = key;
        const leadEl = document.createElement('span');
        leadEl.className = 'mm-box__lead';
        leadEl.textContent = lead;
        message.replaceChildren(...(lead ? [leadEl, ' '] : []), rest);
      }

      // One event per milestone and page view, also when a restored or shared box starts above it.
      milestones.forEach((milestone, i) => {
        const key = milestone.type === 'tier' ? `tier-${milestone.percent}` : 'shipping';
        if (i > reached || this.trackedMilestones.has(key) || quiet && !this.sdk) return;
        this.trackedMilestones.add(key);
        if (milestone.type === 'tier') this.track('tier_reached', { discount_percent: milestone.percent, threshold: milestone.at / 100, box_value: subtotal / 100, box_count: this.count });
        else this.track('free_shipping_reached', { threshold: this.freeShipping / 100, box_value: subtotal / 100, box_count: this.count });
      });

      if (!quiet && reached > this.lastReached && reached >= 0) {
        this.box.classList.remove('is-unlocked');
        void this.box.offsetWidth;
        this.box.classList.add('is-unlocked');
      }
      this.lastReached = reached;
    }

    renderList() {
      const list = this.querySelector('[data-mm-list]');
      const rows = new Map([...list.children].map((row) => [row.dataset.variant, row]));
      [...this.qty].forEach(([id, q]) => {
        const product = this.products.get(id);
        let row = rows.get(id);
        if (!row) {
          row = document.createElement('li');
          row.className = 'mm-box__item';
          row.dataset.variant = id;
          row.innerHTML = `
            <img class="mm-box__thumb" alt="" width="48" height="48" loading="lazy">
            <div class="mm-box__info"><span class="mm-box__name"></span><span class="mm-box__unit"></span></div>
            <div class="mm-box__stepper">
              <button type="button" class="mm-box__step" data-mm-step="-1"><span aria-hidden="true">−</span></button>
              <span class="mm-box__qty" data-mm-qty></span>
              <button type="button" class="mm-box__step" data-mm-step="1"><span aria-hidden="true">+</span></button>
            </div>
            <span class="mm-box__line"></span>`;
          if (product.image) row.querySelector('img').src = product.image;
          else row.querySelector('img').remove();
          row.querySelector('.mm-box__name').textContent = product.title;
          row.querySelector('.mm-box__unit').textContent = product.unit;
          const [dec, inc] = row.querySelectorAll('[data-mm-step]');
          dec.setAttribute('aria-label', this.fill(this.strings.decrease, { product: product.title }));
          inc.setAttribute('aria-label', this.fill(this.strings.increase, { product: product.title }));
        }
        row.querySelector('[data-mm-qty]').textContent = q;
        row.querySelector('.mm-box__line').textContent = this.money(q * product.price);
        list.appendChild(row); // keeps insertion order, moves nothing that is already in place
        rows.delete(id);
      });
      rows.forEach((row) => row.remove());
    }

    showError(text) {
      const error = this.querySelector('[data-mm-error]');
      error.textContent = text;
      error.hidden = false;
    }

    hideError() {
      this.querySelector('[data-mm-error]').hidden = true;
    }

    /* ---------- Easy Bundle SDK ---------- */

    waitForSdk() {
      // Easy Bundle's API answers 429/404 now and then; retry before giving up.
      const start = async () => {
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            return await this.connectSdk();
          } catch (error) {
            console.warn(`Mix & Match: SDK connect attempt ${attempt} failed`, error);
            if (attempt === 3) return this.failSdk(error);
            await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
          }
        }
      };
      // The app embed injects the SDK late on slow phones; give up only after a generous wait.
      this.sdkTimer = setTimeout(() => { if (!this.sdk) this.failSdk(new Error('gbbSdk did not load')); }, 45000);
      if (window.gbbSdk) start();
      else document.addEventListener('gbb-sdk:loaded', start, { once: true });
    }

    async connectSdk() {
      let sdk = null;
      try {
        sdk = await window.gbbSdk.init({
          // Every category up front: the shopper may tap any card in any tab.
          initFetch: (steps) => Object.fromEntries(Object.keys(steps).map((id) => [id, 'all'])),
        });
      } catch (error) {
        // The bundle's design template may have initialized first and consumed the
        // pre-resolved bundle data; init() then throws "bundleData is required".
        // Its instance is stored on gbbSdk under the offer id, so share that one.
        sdk = await this.existingSdk();
        if (!sdk) throw error;
      }
      if (!sdk) throw new Error('gbbSdk.init() returned no instance');
      await this.loadAllProductsOf(sdk);
      this.sdk = sdk;
      clearTimeout(this.sdkTimer);
      this.applySdkTiers();
      this.mapVariants();

      const E = window.gbbSdk.events;
      window.gbbSdk.subscribe(E.CART_ITEM_UPDATED, () => this.onSdkCart());
      window.gbbSdk.subscribe(E.BUNDLE_STATE_RESET, () => this.onSdkCart());

      // Replay taps made while the SDK was loading (and a restored box).
      [...this.qty.keys()].forEach((id) => this.syncVariant(id));
      this.onSdkCart();
      this.toggleAttribute('data-ready', true);
      this.dispatchEvent(new CustomEvent('mix-match:ready', { bubbles: true }));
      this.readyResolve?.();
    }

    failSdk(error) {
      if (this.sdk || this.sdkFailed) return;
      console.error('Mix & Match: Easy Bundle SDK failed', error);
      this.sdkFailed = true;
      this.sdkError = String(error?.stack || error);
      this.readyReject?.(error);
    }

    ready() {
      if (this.sdk) return Promise.resolve();
      if (this.sdkFailed) return Promise.reject(new Error('sdk failed'));
      this.readyPromise ??= new Promise((resolve, reject) => {
        this.readyResolve = resolve;
        this.readyReject = reject;
      });
      return this.readyPromise;
    }

    async existingSdk() {
      const offerId = window.shopifyLiquidValuesApp4Ext?.product?.bundleProductConfiguration?.offerId;
      const keys = offerId ? [offerId] : Object.keys(window.gbbSdk).filter((key) => /^(SDK|PB)-/.test(key));
      for (const key of keys) {
        const candidate = await window.gbbSdk[key];
        if (candidate?.f?.getAllStepsData) return candidate;
      }
      return null;
    }

    async loadAllProductsOf(sdk) {
      // Categories page in their products; keep fetching until every page is in.
      for (let round = 0; round < 20; round++) {
        const pending = sdk.category.f.getAll().filter((cat) => {
          const data = sdk.category.f.getData(cat.categoryId);
          return data && (data.hasNextPage || !data.initialDataReady);
        });
        if (!pending.length) return;
        await sdk.f.fetchProducts(pending.map((cat) => cat.categoryId));
      }
    }

    applySdkTiers() {
      const rules = this.sdk.discount.state.discountConfiguration?.rules;
      if (!Array.isArray(rules) || !rules.every((rule) => rule.type === 'amount')) return;
      const tiers = rules
        .map((rule) => ({ min: Math.round(Number(rule.value) * 100), percent: Number(rule.discountValue) }))
        .filter((tier) => tier.min > 0 && tier.percent > 0)
        .sort((a, b) => a.min - b.min);
      if (!tiers.length || JSON.stringify(tiers) === JSON.stringify(this.tiers)) return;
      // Easy Bundle decides the real discount, so its tiers win over the section settings.
      this.tiers = tiers;
      this.buildMarkers();
      console.warn('Mix & Match: section tiers differ from Easy Bundle, using Easy Bundle', tiers);
      this.render({ quiet: true });
    }

    mapVariants() {
      this.sdk.f.getAllStepsData().forEach((step) => {
        Object.values(step.categories).forEach((cat) => {
          (this.sdk.category.f.getProducts(cat.categoryId) || []).forEach((product) => {
            (product.variants || []).forEach((variant) => {
              const id = String(variant.variantId || variant.id);
              if (!this.routes.has(id)) this.routes.set(id, { categoryId: cat.categoryId, stepId: step.stepId });
            });
          });
        });
      });
      // Keep sold-out packs visible even when the SDK omits them. Hide only in-stock
      // packs outside this bundle, which cannot be added through its SDK.
      const missing = [];
      this.controls.forEach(({ control, packs, group }) => {
        packs.forEach((pack) => {
          if (this.routes.has(pack.dataset.variant)) return;
          this.qty.delete(pack.dataset.variant);
          if (pack.dataset.available !== 'true') return;
          missing.push(`${control.dataset.title} (${pack.dataset.unit})`);
          pack.hidden = true;
        });
        const left = packs.filter((pack) => !pack.hidden);
        if (!left.length) {
          control.closest('.mm__item')?.setAttribute('hidden', '');
          return;
        }
        group.hidden = false;
        if (!left.some((pack) => pack.dataset.variant === control.dataset.variant)) {
          this.selectPack(left.find((pack) => pack.dataset.available === 'true') || left[0], { allowUnavailable: true });
        }
      });
      if (missing.length) console.warn('Mix & Match: not in the Easy Bundle bundle, hidden:', missing);
      this.checkEmpty();
      this.persist();
      this.render({ quiet: true });
    }

    sdkLine(variantId) {
      const route = this.routes.get(variantId);
      if (!route) return null;
      return this.sdk.f.findExistingVariantInCart({ categoryId: route.categoryId, variants: [{ variantId }] }) || null;
    }

    syncVariant(variantId) {
      if (!this.sdk) return;
      const route = this.routes.get(variantId);
      if (!route) return;
      const want = this.qty.get(variantId) || 0;
      const line = this.sdkLine(variantId);
      const have = line?.quantity || 0;
      if (want === have) return;
      let result;
      if (line) {
        result = this.sdk.f.updateCartItem(line.properties._uniqueGbbSdkItemKey, { quantity: want, showToast: false });
      } else {
        result = this.sdk.f.addProductToBundle({ ...route, variantId, quantity: want, showToast: false });
      }
      if (!result?.ok) {
        // The SDK refused (stock, limits): fall back to what the bundle really holds.
        console.warn('Mix & Match: SDK refused', variantId, result?.data);
        if (have > 0) this.qty.set(variantId, have);
        else this.qty.delete(variantId);
        this.persist();
        this.render({ quiet: true });
        this.showError(result?.data?.message || this.strings.error);
      }
    }

    onSdkCart() {
      this.sdkCart = this.sdk.f.getCartData() || null;
      this.render({ quiet: true });
    }

    /* ---------- add to cart ---------- */

    async addToCart() {
      if (this.busy || this.count === 0) return;
      const button = this.querySelector('[data-mm-atc]');
      const label = this.querySelector('[data-mm-atc-label]');
      this.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.hideError();
      try {
        if (!this.sdk) {
          label.textContent = this.strings.preparing;
          await this.ready();
        }
        label.textContent = this.strings.adding;
        [...this.qty.keys()].forEach((id) => this.syncVariant(id));
        const result = await this.sdk.f.addBundleToCart({ showToast: false });
        if (!result?.ok) throw new Error(result?.data?.code || 'ADD_FAILED');
        const total = this.sdkCart?.discounted_price;
        this.track('box_added_to_cart', { ...this.boxData(), box_total: (Number.isFinite(total) ? total : this.subtotal) / 100 });
        this.qty.clear();
        this.persist();
        this.toggleBox(false);
        await this.refreshCart();
      } catch (error) {
        console.error('Mix & Match: add to cart failed', error);
        this.showError(this.strings.error);
      } finally {
        this.busy = false;
        button.removeAttribute('aria-busy');
        label.textContent = this.strings.cta;
        this.render({ quiet: true });
      }
    }

    async refreshCart() {
      // Same refresh the product cards use: re-render drawer + bubble, then open the drawer.
      try {
        const sections = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer,cart-icon-bubble`).then((r) => r.json());
        const drawer = document.querySelector('cart-drawer');
        if (drawer && sections['cart-drawer']) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ sections }, { open: true });
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }
        const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mix-match', cartData: cart, sectionsRendered: true });
      } catch (error) {
        console.error('Mix & Match: cart display could not refresh', error);
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    }
  });
}
